// Müşteriler, sözleşmeler, duyurular (C38–C41, C46–C48): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Kurum = `type: INSTITUTION` firma kaydı; sözleşme servis firması ↔ kurum arasındadır. Para kuruş (Int), tarih YYYY-AA-GG.
import { z } from 'zod';
import {
  ANNOUNCEMENT_CHANNELS,
  type AnnouncementChannel,
  type ComplaintCategory,
  type ComplaintStatus,
} from '../enums';
import { IsoDate, IsoDateTime, Phone, type Paged } from './common';

// ───────── müşteri (kurum) türü ─────────

export const CUSTOMER_KINDS = ['SCHOOL', 'CORPORATE', 'HOSPITAL', 'UNIVERSITY', 'OTHER'] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];
export const CUSTOMER_KIND_LABELS: Record<CustomerKind, string> = {
  SCHOOL: 'Okul',
  CORPORATE: 'Şirket',
  HOSPITAL: 'Hastane',
  UNIVERSITY: 'Üniversite',
  OTHER: 'Diğer',
};

// ───────── sözleşme ─────────

export const CONTRACT_PRICING_MODELS = ['PER_VEHICLE_MONTH', 'PER_KM', 'PER_PASSENGER_MONTH', 'PER_TRIP'] as const;
export type ContractPricingModel = (typeof CONTRACT_PRICING_MODELS)[number];
export const CONTRACT_PRICING_LABELS: Record<ContractPricingModel, string> = {
  PER_VEHICLE_MONTH: 'Araç başı aylık',
  PER_KM: 'Kilometre başı',
  PER_PASSENGER_MONTH: 'Yolcu başı aylık',
  PER_TRIP: 'Sefer başı',
};
/** Birim fiyatın yanında gösterilen birim ("78.000 ₺ / araç · ay"). */
export const CONTRACT_PRICING_UNITS: Record<ContractPricingModel, string> = {
  PER_VEHICLE_MONTH: 'araç · ay',
  PER_KM: 'km',
  PER_PASSENGER_MONTH: 'yolcu · ay',
  PER_TRIP: 'sefer',
};
export const CONTRACT_PRICING_HINTS: Record<ContractPricingModel, string> = {
  PER_VEHICLE_MONTH: 'Sabit araç sayısıyla düzenli servis. En yaygın model.',
  PER_KM: 'Gerçekleşen kilometreye göre aylık hakediş.',
  PER_PASSENGER_MONTH: 'Kayıtlı yolcu sayısına göre; okul servislerinde yaygın.',
  PER_TRIP: 'Vardiya ya da etkinlik gibi seyrek seferler için.',
};

/** Veritabanındaki sözleşme durumu. Fesih servis firmasının kararıdır, denetim kaydıyla. */
export const CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Ekranda gösterilen durum: tarihlerden türetilir (yenileme penceresi 60 gün). */
export const CONTRACT_STATES = [
  'UPCOMING',
  'ACTIVE',
  'EXPIRING',
  'TERMINATING',
  'EXPIRED',
  'TERMINATED',
  'DRAFT',
] as const;
export type ContractState = (typeof CONTRACT_STATES)[number];
export const CONTRACT_STATE_LABELS: Record<ContractState, string> = {
  UPCOMING: 'Başlayacak',
  ACTIVE: 'Yürürlükte',
  EXPIRING: 'Yenileme yaklaşıyor',
  TERMINATING: 'Fesih bildirildi',
  EXPIRED: 'Süresi doldu',
  TERMINATED: 'Feshedildi',
  DRAFT: 'Taslak',
};
export const CONTRACT_RENEWAL_WINDOW_DAYS = 60;

function dayDiff(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

/**
 * Sözleşmenin bugünkü görünür durumu (`today` İstanbul günü, YYYY-AA-GG). Bitiş günü dahil yürürlüktedir.
 * Fesih ileri tarihliyse o güne kadar hizmet sürer ("Fesih bildirildi").
 */
export function contractState(c: { status: string; startsAt: string; endsAt: string }, today: string): ContractState {
  if (c.status === 'TERMINATED') return c.endsAt >= today ? 'TERMINATING' : 'TERMINATED';
  if (c.status === 'DRAFT') return 'DRAFT';
  if (c.status === 'EXPIRED' || c.endsAt < today) return 'EXPIRED';
  if (c.startsAt > today) return 'UPCOMING';
  return dayDiff(today, c.endsAt) <= CONTRACT_RENEWAL_WINDOW_DAYS ? 'EXPIRING' : 'ACTIVE';
}

/** Bitişe kalan gün (geçmişse negatif). */
export function contractDaysLeft(endsAt: string, today: string): number {
  return dayDiff(today, endsAt);
}

const money = (what: string, min = 0) =>
  z
    .number()
    .int(`${what} kuruş olarak tam sayı olmalı`)
    .min(min, min > 0 ? `${what} girin` : `${what} negatif olamaz`)
    .max(10_000_000_000, `${what} çok yüksek`);

const contractTermsShape = {
  startsAt: IsoDate,
  endsAt: IsoDate,
  pricingModel: z.enum(CONTRACT_PRICING_MODELS),
  /** kuruş */
  unitPrice: money('Birim fiyat', 1),
  extraKmPrice: money('Ek km fiyatı').default(0),
  extraTripPrice: money('Ek sefer fiyatı').default(0),
  slaOnTimePct: z.number().int().min(50, 'Hedef en az %50 olmalı').max(100, 'Hedef en fazla %100 olabilir').default(95),
  slaGpsPct: z.number().int().min(50, 'Hedef en az %50 olmalı').max(100, 'Hedef en fazla %100 olabilir').default(98),
  slaMaxDelayMin: z.number().int().min(1, 'En az 1 dk olmalı').max(60, 'En fazla 60 dk olabilir').default(5),
  notes: z.string().trim().max(1000, 'Not en fazla 1000 karakter olabilir').optional(),
};

function checkPeriod(v: { startsAt: string; endsAt: string }, ctx: z.RefinementCtx, path: string[] = []) {
  if (v.endsAt <= v.startsAt)
    ctx.addIssue({ code: 'custom', message: 'Bitiş tarihi başlangıçtan sonra olmalı.', path: [...path, 'endsAt'] });
  else if (dayDiff(v.startsAt, v.endsAt) > 5 * 366)
    ctx.addIssue({ code: 'custom', message: 'Sözleşme süresi en fazla 5 yıl olabilir.', path: [...path, 'endsAt'] });
}

export const ContractTermsInput = z.object(contractTermsShape).superRefine((v, ctx) => checkPeriod(v, ctx));
export type ContractTermsInput = z.input<typeof ContractTermsInput>;

/** C39 "Yeni sözleşme": var olan müşteriye yeni dönem sözleşmesi. */
export const ContractCreateInput = z
  .object({ institutionId: z.string().min(1).max(64), ...contractTermsShape })
  .superRefine((v, ctx) => checkPeriod(v, ctx));
export type ContractCreateInput = z.input<typeof ContractCreateInput>;

/** Yenile: bitiş tarihi uzar; istenirse birim fiyat ve zamanında hedefi güncellenir. */
export const ContractRenewInput = z.object({
  endsAt: IsoDate,
  unitPrice: money('Birim fiyat', 1).optional(),
  slaOnTimePct: z.number().int().min(50).max(100).optional(),
  note: z.string().trim().max(300).optional(),
});
export type ContractRenewInput = z.input<typeof ContractRenewInput>;

/** Feshet: geçerlilik tarihi (bugün veya sonrası) ve gerekçe. */
export const ContractTerminateInput = z.object({
  effectiveAt: IsoDate,
  reason: z.string().trim().min(5, 'Fesih gerekçesini yazın (en az 5 karakter).').max(300),
});
export type ContractTerminateInput = z.input<typeof ContractTerminateInput>;

/** Sözleşme belgesi (PDF/görsel, /v1/uploads ile yüklenmiş dosya). `null` belgeyi kaldırır. */
export const ContractDocumentInput = z.object({ fileId: z.string().min(1).max(64).nullable() });
export type ContractDocumentInput = z.infer<typeof ContractDocumentInput>;

// ───────── müşteri girdileri ─────────

const PersonName = (what: string) =>
  z.string().trim().min(2, `${what} en az 2 harf olmalı`).max(60, `${what} en fazla 60 harf olabilir`);

export const CUSTOMER_ROLES = ['INST_ADMIN', 'INST_VIEWER'] as const;
export type CustomerRole = (typeof CUSTOMER_ROLES)[number];
export const CUSTOMER_ROLE_LABELS: Record<CustomerRole, string> = {
  INST_ADMIN: 'Kurum yöneticisi',
  INST_VIEWER: 'Kurum izleyici',
};

/** C39 "Kurum yöneticisini davet et": telefonla kurum paneline yetkili ekler, SMS daveti gider. */
export const CustomerInviteInput = z.object({
  firstName: PersonName('Ad'),
  lastName: PersonName('Soyad'),
  phone: Phone,
  title: z.string().trim().max(60, 'Unvan en fazla 60 karakter olabilir').optional(),
  role: z.enum(CUSTOMER_ROLES).default('INST_ADMIN'),
});
export type CustomerInviteInput = z.input<typeof CustomerInviteInput>;

/** C40 Yeni müşteri: kurum + ilk sözleşme (+ isteğe bağlı ilk yönetici) tek işlemde. */
export const CustomerCreateInput = z
  .object({
    name: z.string().trim().min(2, 'Kurum adı en az 2 karakter olmalı').max(120, 'Kurum adı en fazla 120 karakter'),
    kind: z.enum(CUSTOMER_KINDS),
    legalName: z.string().trim().max(200, 'Ticari unvan en fazla 200 karakter').optional(),
    taxNo: z
      .string()
      .trim()
      .regex(/^\d{10,11}$/, 'Vergi numarası 10 haneli olmalı (şahıs şirketinde 11 haneli T.C. kimlik no).')
      .optional(),
    city: z.string().trim().min(2).max(60).default('İstanbul'),
    address: z.string().trim().max(300, 'Adres en fazla 300 karakter').optional(),
    phone: z
      .string()
      .regex(/^\+90\d{10}$/, 'Telefon +90 ile başlamalı ve 10 haneli olmalı')
      .optional(),
    email: z.string().trim().max(160).email('Geçerli bir e-posta adresi girin').optional(),
    contract: z.object(contractTermsShape),
    admin: CustomerInviteInput.optional(),
  })
  .superRefine((v, ctx) => checkPeriod(v.contract, ctx, ['contract']));
export type CustomerCreateInput = z.input<typeof CustomerCreateInput>;

export const CustomerListQuery = z.object({
  q: z.string().trim().max(80).optional(),
  kind: z.enum(CUSTOMER_KINDS).optional(),
  contract: z.enum(['all', 'active', 'expiring', 'inactive']).default('all'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type CustomerListQuery = z.input<typeof CustomerListQuery>;

// ───────── yanıtlar ─────────

export interface ContractSummary {
  id: string;
  number: string;
  status: ContractStatus;
  state: ContractState;
  startsAt: string;
  endsAt: string;
  daysLeft: number;
  pricingModel: ContractPricingModel;
  /** kuruş */
  unitPrice: number;
  slaOnTimePct: number;
  routes: number;
}

export interface CustomerRow {
  id: string;
  name: string;
  kind: CustomerKind | null;
  city: string;
  /** bugün yürürlükteki (yoksa en güncel) sözleşme */
  contract: ContractSummary | null;
  contracts: number;
  routes: number;
  /** bu firmanın rotalarına atanmış aktif yolcu */
  passengers: number;
  todayTrips: number;
  /** bugün başlamış (yolda veya tamamlanmış) sefer */
  todayStarted: number;
  /** bugün başlamış seferlerde zamanında oranı (gecikme uyarı eşiğinin altında) */
  todayOnTimePct: number | null;
  openComplaints: number;
}

export interface CustomerList extends Paged<CustomerRow> {
  counts: { all: number; active: number; expiring: number; inactive: number };
}

/** Hizmet performansı (tamamlanan seferlerden). */
export interface ServicePerformance {
  from: string;
  to: string;
  trips: number;
  /** gecikme eşiğinin (dk) altında tamamlanan sefer oranı */
  onTimePct: number | null;
  onTimeThresholdMin: number;
  /** binen yolcu / araç kapasitesi ortalaması */
  occupancyPct: number | null;
  noShowPct: number | null;
  /** duraklarının en az %90'ına varışı GPS ile kaydedilen sefer oranı */
  trackedPct: number | null;
  /** geç kalan seferlerde ortalama gecikme */
  avgDelayMin: number | null;
  daily: { date: string; trips: number; onTimePct: number | null }[];
}

export interface CustomerRouteRow {
  id: string;
  code: string;
  name: string;
  status: string;
  contractId: string;
  contractNumber: string;
  plates: string[];
  passengers: number;
}

export interface CustomerContact {
  membershipId: string;
  name: string;
  title: string | null;
  role: string;
  roleLabel: string;
  /** KVKK: yalnız maskeli */
  phoneMasked: string;
  status: string;
  lastLoginAt: string | null;
}

export interface CustomerComplaintRow {
  id: string;
  number: string;
  category: ComplaintCategory;
  status: ComplaintStatus;
  createdAt: string;
  routeCode: string | null;
}

export interface CustomerGroupCount {
  id: string | null;
  name: string;
  type: string | null;
  count: number;
}

export interface CustomerDetail {
  id: string;
  name: string;
  legalName: string | null;
  kind: CustomerKind | null;
  taxNo: string | null;
  city: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  status: string;
  createdAt: string;
  performance: ServicePerformance;
  activeRoutes: number;
  vehicles: number;
  passengers: number;
  openComplaints: number;
  contracts: ContractSummary[];
  routes: CustomerRouteRow[];
  groups: CustomerGroupCount[];
  contacts: CustomerContact[];
  complaints: CustomerComplaintRow[];
  /** kurum paneline yetkili davet edilebilir mi; değilse nedeni */
  invite: { allowed: boolean; reason: string | null };
}

export interface CustomerCreateResult {
  id: string;
  contractId: string;
  contractNumber: string;
  invited: boolean;
}

export interface SlaMetric {
  key: 'ON_TIME' | 'TRACKING';
  label: string;
  target: number;
  actual: number | null;
  met: boolean | null;
  hint: string;
}

export interface ContractHistoryItem {
  at: string;
  action: string;
  label: string;
  by: string | null;
  detail: string | null;
}

export interface ContractDocument {
  fileId: string;
  url: string;
  mime: string;
  size: number;
  uploadedAt: string;
}

export interface ContractDetail extends ContractSummary {
  provider: { id: string; name: string; legalName: string | null; taxNo: string | null };
  institution: {
    id: string;
    name: string;
    legalName: string | null;
    kind: CustomerKind | null;
    address: string | null;
  };
  extraKmPrice: number;
  extraTripPrice: number;
  slaGpsPct: number;
  slaMaxDelayMin: number;
  notes: string | null;
  createdAt: string;
  sla: SlaMetric[];
  performance: ServicePerformance;
  routesCovered: CustomerRouteRow[];
  document: ContractDocument | null;
  history: ContractHistoryItem[];
}

// ───────── duyurular (C46–C48) ─────────

/**
 * Hedef kitle. `PASSENGERS` = firmanın rotalarındaki tüm yolcular (öğrencide veli), `ALL` = yolcular + şoför/rehberler.
 * Şoför uygulaması `ALL` ve `DRIVERS` duyurularını listeler (kiracı kuralı).
 */
export const ANNOUNCEMENT_TARGETS = [
  'PASSENGERS',
  'INSTITUTION',
  'ROUTE',
  'GROUP',
  'VEHICLE',
  'PARENTS',
  'DRIVERS',
  'ALL',
] as const;
export type AnnouncementTarget = (typeof ANNOUNCEMENT_TARGETS)[number];
export const ANNOUNCEMENT_TARGET_LABELS: Record<AnnouncementTarget, string> = {
  PASSENGERS: 'Tüm yolcular',
  INSTITUTION: 'Kurum',
  ROUTE: 'Rota',
  GROUP: 'Grup',
  VEHICLE: 'Araç',
  PARENTS: 'Veliler',
  DRIVERS: 'Şoförler',
  ALL: 'Herkes',
};
export const ANNOUNCEMENT_TARGET_HINTS: Record<AnnouncementTarget, string> = {
  PASSENGERS: 'Rotalarınızdaki tüm yolcular; öğrencilerde veliler.',
  INSTITUTION: 'Seçtiğiniz kurumun yolcuları.',
  ROUTE: 'Seçtiğiniz rotadaki yolcular.',
  GROUP: 'Bölüm, sınıf veya vardiya grubu.',
  VEHICLE: 'Seçtiğiniz araçla taşınan yolcular.',
  PARENTS: 'Öğrenci velileri.',
  DRIVERS: 'Şoför uygulamasındaki şoför ve rehberler.',
  ALL: 'Yolcular, veliler, şoför ve rehberler.',
};
/** Seçim gerektiren hedefler (kurum/rota/grup/araç kimliği). */
export const ANNOUNCEMENT_TARGETS_WITH_REF: readonly AnnouncementTarget[] = [
  'INSTITUTION',
  'ROUTE',
  'GROUP',
  'VEHICLE',
];

export const ANNOUNCEMENT_CHANNEL_LABELS: Record<AnnouncementChannel, string> = {
  PUSH: 'Push',
  SMS: 'SMS',
  IN_APP: 'Uygulama içi',
};

export const ANNOUNCEMENT_STATES = ['DRAFT', 'SCHEDULED', 'SENT'] as const;
export type AnnouncementState = (typeof ANNOUNCEMENT_STATES)[number];
export const ANNOUNCEMENT_STATE_LABELS: Record<AnnouncementState, string> = {
  DRAFT: 'Taslak',
  SCHEDULED: 'Zamanlandı',
  SENT: 'Gönderildi',
};

export const ANNOUNCEMENT_TITLE_MAX = 80;
export const ANNOUNCEMENT_BODY_MAX = 600;
/** SMS en fazla bu kadar parçaya bölünebilir (maliyet ve okunabilirlik). */
export const ANNOUNCEMENT_SMS_MAX_SEGMENTS = 4;

const announcementShape = {
  target: z.enum(ANNOUNCEMENT_TARGETS),
  targetRef: z.string().min(1).max(64).optional(),
  channels: z
    .array(z.enum(ANNOUNCEMENT_CHANNELS))
    .min(1, 'En az bir kanal seçin.')
    .max(3)
    .refine((a) => new Set(a).size === a.length, 'Kanal iki kez seçilemez.'),
};

function checkRef(v: { target: AnnouncementTarget; targetRef?: string }, ctx: z.RefinementCtx) {
  if (ANNOUNCEMENT_TARGETS_WITH_REF.includes(v.target) && !v.targetRef)
    ctx.addIssue({
      code: 'custom',
      message: `${ANNOUNCEMENT_TARGET_LABELS[v.target]} seçin.`,
      path: ['targetRef'],
    });
}

export const AnnouncementInput = z
  .object({
    ...announcementShape,
    title: z
      .string()
      .trim()
      .min(3, 'Başlık en az 3 karakter olmalı')
      .max(ANNOUNCEMENT_TITLE_MAX, `Başlık en fazla ${ANNOUNCEMENT_TITLE_MAX} karakter`),
    body: z
      .string()
      .trim()
      .min(3, 'Duyuru metni en az 3 karakter olmalı')
      .max(ANNOUNCEMENT_BODY_MAX, `Metin en fazla ${ANNOUNCEMENT_BODY_MAX} karakter`),
    /** ileri tarihli gönderim (yoksa hemen) */
    sendAt: IsoDateTime.optional(),
    /** taslak olarak kaydet (gönderme) */
    draft: z.boolean().default(false),
  })
  .superRefine(checkRef);
export type AnnouncementInput = z.input<typeof AnnouncementInput>;

export const AnnouncementPreviewInput = z.object(announcementShape).superRefine(checkRef);
export type AnnouncementPreviewInput = z.input<typeof AnnouncementPreviewInput>;

export const AnnouncementQuery = z.object({
  state: z.enum(['all', ...ANNOUNCEMENT_STATES]).default('all'),
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type AnnouncementQuery = z.input<typeof AnnouncementQuery>;

/** Gönderim sonucu (denetim kaydında saklanır). */
export interface AnnouncementStats {
  /** ulaşılan tekil kişi (uygulama + yalnız SMS) */
  recipients: number;
  /** uygulama hesabıyla ulaşılan (push / uygulama içi) */
  appUsers: number;
  passengers: number;
  guardians: number;
  staff: number;
  /** uygulama içi kutuya düşen (kullanıcı tercihi kapalı olanlar hariç; push ile birlikte) */
  inApp: number;
  push: boolean;
  sms: number;
  smsSegments: number;
}

export interface AnnouncementRow {
  id: string;
  title: string;
  body: string;
  target: AnnouncementTarget;
  targetRef: string | null;
  targetLabel: string;
  channels: AnnouncementChannel[];
  state: AnnouncementState;
  scheduledAt: string | null;
  sentAt: string | null;
  createdAt: string;
  author: string | null;
  recipientCount: number;
  /** uygulama içi teslim ve okunma (bildirim kutusu) */
  delivered: number;
  read: number;
  readRate: number | null;
}

export interface AnnouncementList extends Paged<AnnouncementRow> {
  counts: Record<'all' | AnnouncementState, number>;
  /** son 30 gün özeti */
  last30: { sent: number; recipients: number; readRate: number | null; sms: number };
}

export interface AnnouncementDetail extends AnnouncementRow {
  stats: AnnouncementStats | null;
  push: { title: string; body: string };
  smsText: string;
}

export interface AnnouncementPreview {
  targetLabel: string;
  /** seçili kanallarla ulaşılacak tekil kişi */
  recipients: number;
  /** uygulama hesabı olan alıcı (push / uygulama içi) */
  appUsers: number;
  /** yolcu (öğrencide velisi olan; SMS seçiliyse yalnız telefonu kayıtlı çalışanlar dahil) */
  passengers: number;
  guardians: number;
  staff: number;
  /** SMS seçiliyse gönderilecek tekil telefon */
  smsRecipients: number;
  /** uygulama hesabı olmayıp yalnız SMS ile ulaşılabilecek yolcu (SMS seçili değilse ulaşılamaz) */
  smsOnly: number;
  /** okul modülü kapalıyken atlanan öğrenci */
  skippedStudents: number;
}

export interface AnnouncementOptions {
  sender: string;
  school: boolean;
  institutions: { id: string; name: string; kind: CustomerKind | null }[];
  routes: { id: string; code: string; name: string; institution: string }[];
  groups: { id: string; name: string; type: string; institution: string }[];
  vehicles: { id: string; plate: string }[];
}

// ───────── SMS ve push metni ─────────

const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXT = '^{}\\[~]|€';

export interface SmsInfo {
  /** SMS karakter sayısı (GSM uzantı karakterleri 2 sayılır) */
  chars: number;
  segments: number;
  /** parça başına karakter: GSM 160/153, Türkçe karakterli (UCS-2) 70/67 */
  perSegment: number;
  /** ı, ğ, ş, İ, ç gibi GSM dışı karakter içeriyor */
  unicode: boolean;
}

/** SMS parça hesabı: Türkçe karakter (ı ğ ş İ Ğ Ş ç) varsa 70, yoksa 160 karakterlik parçalar. */
export function smsSegments(text: string): SmsInfo {
  let gsm = 0;
  let unicode = false;
  for (const ch of text) {
    if (GSM7_BASIC.includes(ch)) gsm += 1;
    else if (GSM7_EXT.includes(ch)) gsm += 2;
    else {
      unicode = true;
      break;
    }
  }
  if (unicode) {
    const chars = text.length;
    const perSegment = chars <= 70 ? 70 : 67;
    return { chars, segments: chars ? Math.ceil(chars / perSegment) : 0, perSegment, unicode };
  }
  const perSegment = gsm <= 160 ? 160 : 153;
  return { chars: gsm, segments: gsm ? Math.ceil(gsm / perSegment) : 0, perSegment, unicode };
}

/** "Başlık: metin" (başlık noktalama ile bitiyorsa iki nokta eklenmez). */
export function joinTitleBody(title: string, body: string): string {
  const t = title.trim();
  const b = body.trim();
  if (!b) return t;
  if (!t) return b;
  return /[.!?:]$/.test(t) ? `${t} ${b}` : `${t}: ${b}`;
}

/** Duyuru push/uygulama içi görünümü: başlıkta gönderen firma, gövdede duyuru (04-copy "Duyuru" şablonu). */
export function announcementPush(sender: string, title: string, body: string): { title: string; body: string } {
  return { title: sender, body: joinTitleBody(title, body) };
}

/** Duyuru SMS metni: "Firma: Başlık: metin". */
export function announcementSmsText(sender: string, title: string, body: string): string {
  return `${sender}: ${joinTitleBody(title, body)}`;
}
